/**
 * Responsive action-row overflow math — vendored from scio3
 * `features/mdm-devices/components/actions/action-overflow.ts`. Commands have one priority order; the
 * toolbar shows as many as fit and the rest collapse into "More" only because they do not fit.
 *
 * Pure so the fit logic is unit-tested without a DOM (the component feeds in measured widths).
 */
export interface VisibleCountParams {
  /** Natural pixel widths of each command button, in display order. */
  widths: number[];
  /** Natural pixel width of the "More" trigger (reserved when anything overflows). */
  moreWidth: number;
  /** Available pixel width of the toolbar container. */
  available: number;
  /** Horizontal gap between items, in pixels. */
  gap: number;
}

/**
 * How many leading buttons fit in the toolbar.
 * - All fit → returns `widths.length` (caller shows everything, no "More").
 * - Otherwise reserves `moreWidth` and returns how many fit beside it (min 1).
 * - `available <= 0` (no layout yet, e.g. jsdom) → returns `widths.length` so
 *   nothing is hidden before measurement runs.
 */
export function computeVisibleCount({
  widths,
  moreWidth,
  available,
  gap,
}: VisibleCountParams): number {
  if (widths.length === 0) return 0;
  if (available <= 0) return widths.length;

  const totalAll = widths.reduce((sum, w) => sum + w, 0) + gap * Math.max(0, widths.length - 1);
  if (totalAll <= available) return widths.length;

  let used = moreWidth;
  let count = 0;
  for (let i = 0; i < widths.length; i++) {
    const w = widths[i] + gap;
    if (used + w <= available) {
      used += w;
      count += 1;
    } else {
      break;
    }
  }
  return Math.max(1, count);
}
